/**
 * Thin async wrapper around a Stockfish web worker speaking UCI.
 *
 * The Stockfish 18 JS file is itself a worker entry point: instantiating
 * `new Worker(path)` boots the engine. Communication is line-based strings.
 */

export interface EngineOptions {
  /** Public URL of the Stockfish JS worker entry. Defaults to the lite single-threaded build. */
  enginePath?: string;
}

export interface SearchParams {
  /** Stockfish "Skill Level" option, clamped to [0, 20]. */
  skill: number;
  /** Per-move thinking budget in milliseconds. */
  movetimeMs: number;
}

const DEFAULT_ENGINE_PATH = "assets/engine/stockfish-18-lite-single.js";

export class StockfishEngine {
  private worker: Worker;
  private readyPromise: Promise<void>;
  private listeners: Array<(line: string) => void> = [];
  private currentSkill = -1;
  private disposed = false;

  constructor(opts: EngineOptions = {}) {
    const path = opts.enginePath ?? DEFAULT_ENGINE_PATH;
    this.worker = new Worker(path);
    this.worker.onmessage = (e: MessageEvent) => {
      const data = e.data;
      const text = typeof data === "string" ? data : String(data);
      // Stockfish may send multi-line strings; split for line-based parsing.
      for (const line of text.split("\n")) {
        if (!line) continue;
        for (const l of this.listeners.slice()) l(line);
      }
    };
    this.worker.onerror = () => {
      // Surface to in-flight waiters by clearing them; they will time out.
      this.listeners = [];
    };
    this.readyPromise = this.handshake();
  }

  private send(cmd: string): void {
    if (this.disposed) return;
    this.worker.postMessage(cmd);
  }

  private waitFor(predicate: (line: string) => boolean, timeoutMs: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const onLine = (line: string) => {
        if (!predicate(line)) return;
        cleanup();
        resolve(line);
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error("engine: timed out waiting for response"));
      }, timeoutMs);
      const cleanup = () => {
        clearTimeout(timer);
        this.listeners = this.listeners.filter((l) => l !== onLine);
      };
      this.listeners.push(onLine);
    });
  }

  private async handshake(): Promise<void> {
    this.send("uci");
    await this.waitFor((l) => l === "uciok", 20000);
    this.send("isready");
    await this.waitFor((l) => l === "readyok", 20000);
  }

  /** Wait until the engine has completed its UCI handshake. */
  ready(): Promise<void> {
    return this.readyPromise;
  }

  private async setSkill(skill: number): Promise<void> {
    const s = Math.max(0, Math.min(20, Math.round(skill)));
    if (s === this.currentSkill) return;
    this.send(`setoption name Skill Level value ${s}`);
    this.currentSkill = s;
  }

  /**
   * Ask Stockfish for the best move from a given FEN position.
   * Returns the UCI move string (e.g. "e7e5") or null on timeout/error.
   */
  async bestMove(fen: string, params: SearchParams): Promise<string | null> {
    if (this.disposed) return null;
    try {
      await this.readyPromise;
      await this.setSkill(params.skill);
      this.send(`position fen ${fen}`);
      this.send(`go movetime ${Math.max(50, params.movetimeMs | 0)}`);
      const line = await this.waitFor(
        (l) => l.startsWith("bestmove"),
        params.movetimeMs + 5000,
      );
      const m = line.match(/^bestmove\s+(\S+)/);
      if (!m) return null;
      const uci = m[1];
      if (uci === "(none)" || uci === "0000") return null;
      return uci;
    } catch {
      // Engine misbehaved or timed out: caller should fall back to random.
      this.send("stop");
      return null;
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    try {
      this.send("quit");
      this.worker.terminate();
    } catch {
      /* ignore */
    }
    this.listeners = [];
  }
}

/**
 * Difficulty scaling: every wrap (row survived) makes the opponent stronger.
 *
 * Opening starts at a firm baseline (skill 12) so the enemy plays real tactics
 * from move one instead of blundering, then ramps to full strength (skill 20) by
 * a few wraps in. Per-move thinking time grows from 500ms to a 900ms cap.
 */
export function difficultyForWraps(wraps: number): SearchParams {
  const w = Math.max(0, wraps | 0);
  const skill = Math.min(20, 12 + w * 2);
  const movetimeMs = Math.min(900, 500 + w * 50);
  return { skill, movetimeMs };
}
